import { z } from "zod"

// Marketplace search-ranking contracts (issue #217). A day with
// measured=false was not captured (anti-bot/parser failure) and must never be
// read as "products dropped off the page".

export const SEARCH_RANKING_RUN_STATUS = {
  valid: "valid",
  partial: "partial",
  blocked: "blocked",
  invalid: "invalid",
} as const
export type SearchRankingRunStatus =
  (typeof SEARCH_RANKING_RUN_STATUS)[keyof typeof SEARCH_RANKING_RUN_STATUS]

export const SEARCH_RANKING_RUN_PHASE = {
  cadenceValidation: "cadence_validation",
  baseline: "baseline",
  production: "production",
} as const
export type SearchRankingRunPhase =
  (typeof SEARCH_RANKING_RUN_PHASE)[keyof typeof SEARCH_RANKING_RUN_PHASE]

export const searchMarketplaceIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "must be a lowercase kebab-case marketplace id")

const httpUrlSchema = z
  .string()
  .max(2048)
  .url()
  .refine((value) => {
    const protocol = new URL(value).protocol
    return protocol === "https:" || protocol === "http:"
  }, "must be an http(s) URL")

/** A phrase we track, and — when the marketplace publishes one — the demand
 * behind it.
 *
 * The three demand fields are optional because they come from a marketplace's
 * ads dashboard, and not every marketplace has one to export. Allegro has no
 * Ads API and no obtainable keyword report, so its phrases carry rank evidence
 * and no demand. Absent is stored as null, never as zero. Demand remains import
 * provenance only and is not part of the ranking read contracts. */
const searchKeywordSchema = z
  .object({
    phrase: z.string().trim().min(1).max(200),
    category: z.string().trim().min(1).max(100),
    clicks: z.number().int().nonnegative().optional(),
    impressions: z.number().int().nonnegative().optional(),
    soldUnits: z.number().int().nonnegative().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    // Partial demand is a truncated export, not a marketplace without ads data.
    // Accepting it would put a real impressions count next to a zero-by-omission
    // click count and make the ratio look like a dead phrase.
    const present = [value.clicks, value.impressions, value.soldUnits].filter(
      (metric) => metric !== undefined
    ).length
    if (present !== 0 && present !== 3) {
      context.addIssue({
        code: "custom",
        message:
          "demand must be complete (clicks, impressions and soldUnits) or absent entirely — " +
          "a partially filled export cannot be told apart from real zeroes",
      })
    }
  })

const searchCollectionProfileSchema = z
  .object({
    tag: z.string().trim().min(1).max(50),
    geoCountry: z.string().length(2),
    ipClass: z.string().trim().min(1).max(50),
    runPhase: z.enum(SEARCH_RANKING_RUN_PHASE),
    parserVersion: z.string().trim().min(1).max(100),
    keywordSet: z.string().trim().min(1).max(100),
    /** How many keywords go into one vendor collection. Absent means the whole
     * set in one call, which is what eMAG has always done.
     *
     * Allegro needs 1. Measured: 25 keywords in a single collection get 403 and
     * 429 pages from roughly the halfway mark, while the same 25 sent as 25
     * single-keyword collections back to back — no pauses at all — came back
     * clean. So what the site counts is not our request rate, it is how many
     * requests share one vendor session; a smaller batch buys a fresh one. */
    collectionBatchSize: z.number().int().min(1).max(60).optional(),
    /** Environment variable holding the hosted collector id this profile is
     * collected by. Named per profile rather than derived from the marketplace
     * id, because there is no safe default: falling back to another
     * marketplace's collector would point a parser at pages it cannot read and
     * return an empty grid that looks exactly like a clean run. Absent on
     * profiles that are not vendor-collected. */
    collectorEnv: z
      .string()
      .trim()
      .regex(/^[A-Z][A-Z0-9_]*$/, "must be an upper-snake-case environment variable name")
      .max(100)
      .optional(),
  })
  .strict()

/** Repo-owned config for an observed search marketplace. It is separate from
 * listing marketplace configs because search capture does not imply a feed,
 * offer, or orders integration. */
export const searchMarketplaceConfigSchema = z
  .object({
    schemaVersion: z.literal(1),
    marketplace: searchMarketplaceIdSchema,
    name: z.string().trim().min(1).max(100),
    country: z
      .string()
      .length(2)
      .transform((value) => value.toUpperCase()),
    searchBaseUrl: httpUrlSchema,
    /** Opens the tracker when no marketplace is named — `/keywords` lands here.
     * Optional and unset by default: with one marketplace there is nothing to
     * choose, and without it the landing marketplace would be whichever name
     * sorts first, so adding one could silently move the default. */
    primary: z.boolean().optional(),
    /** IANA zone the capture days are bucketed and reported in. Rank history is
     * read by operators in one market's local day, not UTC. */
    reportingTimeZone: z
      .string()
      .trim()
      .min(1)
      .max(64)
      .regex(/^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/, "must be an IANA time zone"),
    sponsoredLabel: z.string().trim().min(1).max(100),
    /** Three-letter code the marketplace prices in. Stamped onto every captured
     * card, so the collector never has to know which country it is scraping. */
    currency: z
      .string()
      .length(3)
      .regex(/^[A-Za-z]{3}$/, "must be an ISO 4217 alphabetic code")
      .transform((value) => value.toUpperCase()),
    /** Brand phrases removed from competitor title candidates. The extraction
     * stays deterministic and marketplace-local instead of guessing brands
     * from capitalization in the read path. */
    titleOpportunityExcludedBrands: z.array(z.string().trim().min(1).max(100)).max(100).optional(),
    /** Fewest product cards a healthy first page carries. A capture below it is
     * recorded as evidence of a truncated render, not as a ranking.
     *
     * This matters because a half-rendered page is worse than a missing one. Every
     * healthy Allegro page measured carried 72–73 cards; two that answered 200
     * with no challenge were parsed at 54 and at 10. Stored as real, a card that
     * belonged at #60 is simply absent and the report reads it as "not on page
     * one" — the opposite of the truth. Optional: a marketplace with no measured
     * floor should not get a guessed one. */
    minProductCount: z.number().int().min(1).max(200).optional(),
    /** Where the demand numbers came from. Absent for a marketplace that
     * publishes none — see `searchKeywordSchema`. */
    demandSource: z.string().trim().min(1).max(100).optional(),
    defaultCollectionProfileId: z.string().trim().min(1).max(100),
    collectionProfiles: z.record(z.string().trim().min(1).max(100), searchCollectionProfileSchema),
    keywordSets: z.record(
      z.string().trim().min(1).max(100),
      z.array(searchKeywordSchema).min(1).max(60)
    ),
  })
  .strict()
  .superRefine((value, context) => {
    if (!(value.defaultCollectionProfileId in value.collectionProfiles)) {
      context.addIssue({
        code: "custom",
        path: ["defaultCollectionProfileId"],
        message: "must reference a configured collection profile",
      })
    }
    for (const [profileId, profile] of Object.entries(value.collectionProfiles)) {
      if (!(profile.keywordSet in value.keywordSets)) {
        context.addIssue({
          code: "custom",
          path: ["collectionProfiles", profileId, "keywordSet"],
          message: "must reference a configured keyword set",
        })
      }
    }
    for (const [setId, keywords] of Object.entries(value.keywordSets)) {
      const phrases = keywords.map((keyword) => keyword.phrase)
      if (new Set(phrases).size !== phrases.length) {
        context.addIssue({
          code: "custom",
          path: ["keywordSets", setId],
          message: "keyword phrases must be unique within a set",
        })
      }
      // Demand numbers with no named source would be attributed to the
      // marketplace itself in the UI, which is where an operator decides whether
      // to trust them.
      if (value.demandSource === undefined && keywords.some((k) => k.impressions !== undefined)) {
        context.addIssue({
          code: "custom",
          path: ["demandSource"],
          message: `keyword set "${setId}" carries demand numbers, so their source must be named`,
        })
      }
    }
  })

export type SearchMarketplaceConfig = z.infer<typeof searchMarketplaceConfigSchema>
export type SearchKeyword = z.infer<typeof searchKeywordSchema>
export type SearchCollectionProfile = z.infer<typeof searchCollectionProfileSchema>

export type ResolvedSearchCollection = {
  profileId: string
  profile: SearchCollectionProfile
  keywords: SearchKeyword[]
}

/** Single owner of "which profile is this, and which phrases does it capture".
 * The API and the standalone browser scraper both resolve through here so the
 * expected keyword set can never disagree between capture and import. */
export function resolveSearchCollection(
  config: SearchMarketplaceConfig,
  profileId: string = config.defaultCollectionProfileId
): ResolvedSearchCollection {
  const profile = config.collectionProfiles[profileId]
  if (!profile) {
    throw new Error(
      `search collection profile ${config.marketplace}/${profileId} is not configured`
    )
  }
  const keywords = config.keywordSets[profile.keywordSet]
  if (!keywords) {
    throw new Error(
      `search keyword set ${config.marketplace}/${profile.keywordSet} is not configured`
    )
  }
  return { profileId, profile, keywords }
}

/** Every phrase this marketplace tracks, across all profiles, keyed by phrase.
 * The keyword registry's active set is a function of the config alone — one
 * profile importing must never deactivate another profile's phrases. When the
 * same phrase appears in several sets, `preferredKeywordSet` wins so the
 * importing profile's demand figures are the ones stored. */
export function allConfiguredSearchKeywords(
  config: SearchMarketplaceConfig,
  preferredKeywordSet?: string
): Map<string, SearchKeyword> {
  const byPhrase = new Map<string, SearchKeyword>()
  for (const [setId, keywords] of Object.entries(config.keywordSets)) {
    for (const keyword of keywords) {
      if (setId === preferredKeywordSet || !byPhrase.has(keyword.phrase)) {
        byPhrase.set(keyword.phrase, keyword)
      }
    }
  }
  return byPhrase
}

const runIdSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9][a-z0-9-]*$/, "runId must contain lowercase letters, digits, and dashes")
const keywordSchema = z.string().trim().min(1).max(200)
const timestampSchema = z.string().datetime({ offset: true })

const searchRankingImportProductSchema = z
  .object({
    position: z.number().int().min(1).max(200),
    name: z.string().min(1).max(500).nullable(),
    priceCurrent: z.number().nonnegative().nullable(),
    priceOriginal: z.number().nonnegative().nullable(),
    currency: z.string().min(1).max(10).nullable(),
    sponsored: z.boolean(),
    isOwnBrand: z.boolean(),
    url: httpUrlSchema.nullable(),
    rawText: z.string().max(500).nullable(),
  })
  .strict()

/** Which vendor collection served this capture, and where in the run's sequence
 * that collection ran.
 *
 * This is the blast radius of a refusal. A collection is one vendor session, so
 * two keywords collected in the same one saw the same session's treatment, while
 * two keywords in different ones are independent measurements. A profile with
 * `collectionBatchSize: 1` therefore has as many sessions as keywords, and a
 * profile with no batch size has exactly one for the whole run.
 *
 * Nullable because a capture may predate the field, and null is read as "we do
 * not know which session this shared" — the caller falls back to treating the
 * whole run as one, which is the stricter answer.
 */
const searchRankingImportCollectionSchema = z
  .object({
    id: z.string().trim().min(1).max(200),
    /** 0-based position of the collection in the run, in the order they ran.
     * Persistence across consecutive sessions is what tells a site-wide block
     * apart from one unlucky session, so the order has to be recorded, not
     * inferred from timestamps that several captures can share.
     *
     * Bounded well above the keyword cap rather than at it: a run resumed across
     * ticks opens fresh sessions for the keywords still missing, so its sessions
     * outnumber its keywords. */
    index: z.number().int().min(0).max(999),
  })
  .strict()

/** The vendor session a capture came from. One owner for a shape that otherwise
 * gets re-declared at every layer it flows through — the collector's context, the
 * on-disk snapshot, the import payload. */
export type SearchCaptureCollection = z.infer<typeof searchRankingImportCollectionSchema>

const searchRankingImportCaptureSchema = z
  .object({
    keyword: keywordSchema,
    capturedAt: timestampSchema,
    searchUrl: httpUrlSchema,
    gridFound: z.boolean(),
    antiBot: z
      .object({
        httpStatus: z.number().int().min(100).max(599).nullable(),
        captchaDetected: z.boolean(),
        pageTitle: z.string().max(500).nullable(),
      })
      .strict(),
    /** Optional, not required: a payload written by an earlier collector and
     * retried from `pending-import.json` after a deploy must still import.
     * Rejecting it would quarantine real evidence as a validation failure. */
    collection: searchRankingImportCollectionSchema.nullish(),
    products: z.array(searchRankingImportProductSchema).max(100),
  })
  .strict()
  .superRefine((value, context) => {
    const positions = value.products.map((product) => product.position)
    if (new Set(positions).size !== positions.length) {
      context.addIssue({
        code: "custom",
        path: ["products"],
        message: "product positions must be unique within a capture",
      })
    }
  })

/** Trusted import boundary for an untrusted host-side browser capture. The API
 * computes run quality from this evidence; clients cannot declare a run valid. */
export const searchRankingRunImportSchema = z
  .object({
    marketplace: searchMarketplaceIdSchema,
    runId: runIdSchema,
    tag: z.string().min(1).max(50),
    geoCountry: z
      .string()
      .length(2)
      .transform((value) => value.toUpperCase()),
    ipClass: z.string().min(1).max(50),
    collectionProfileId: z.string().min(1).max(100),
    runPhase: z.enum(SEARCH_RANKING_RUN_PHASE),
    parserVersion: z.string().min(1).max(100),
    startedAt: timestampSchema,
    completedAt: timestampSchema,
    expectedKeywords: z.array(keywordSchema).min(1).max(60),
    captures: z.array(searchRankingImportCaptureSchema).max(60),
  })
  .strict()
  .superRefine((value, context) => {
    const startedAt = new Date(value.startedAt).getTime()
    const completedAt = new Date(value.completedAt).getTime()
    if (startedAt > completedAt) {
      context.addIssue({
        code: "custom",
        path: ["completedAt"],
        message: "completedAt must not precede startedAt",
      })
    }
    if (new Set(value.expectedKeywords).size !== value.expectedKeywords.length) {
      context.addIssue({
        code: "custom",
        path: ["expectedKeywords"],
        message: "expectedKeywords must be unique",
      })
    }
    const capturedKeywords = value.captures.map((capture) => capture.keyword)
    if (new Set(capturedKeywords).size !== capturedKeywords.length) {
      context.addIssue({
        code: "custom",
        path: ["captures"],
        message: "captures must contain at most one result per keyword",
      })
    }
    for (const [index, capture] of value.captures.entries()) {
      const capturedAt = new Date(capture.capturedAt).getTime()
      if (capturedAt < startedAt || capturedAt > completedAt) {
        context.addIssue({
          code: "custom",
          path: ["captures", index, "capturedAt"],
          message: "capturedAt must fall within the run interval",
        })
      }
    }
  })

export type SearchRankingRunImport = z.infer<typeof searchRankingRunImportSchema>

/** Statuses that mean the site refused the request rather than answered it.
 *
 * Deliberately a list and not "any 4xx": a 404 is a dead page and a 500 is the
 * site failing, and neither should pause collection. 511 is eMAG's captcha
 * status, 403 and 429 are what Allegro answers with.
 *
 * This exists because a refusal does not have to look like one in the HTML. The
 * same rate-limited Allegro page has been observed both with DataDome markers
 * and without: read only from the body, one of those becomes "the parser found
 * no grid", which does not open the cooldown, and the next tick walks straight
 * back into the limiter. */
const REFUSED_HTTP_STATUS: ReadonlySet<number> = new Set([403, 429, 511])

/** The minimum evidence a capture carries about being turned away. Structural,
 * not marketplace-specific, so the collector can judge a vendor row and the API
 * can judge an imported capture through the same rule. */
export type SearchCaptureBlockSignal = {
  gridFound: boolean
  antiBot: { httpStatus: number | null; captchaDetected: boolean }
}

/**
 * Whether the site turned this capture away, and which way it said so.
 *
 * Grid first, deliberately. A capture that parsed a full results page IS
 * evidence, whatever status or challenge flag came with it — a collector that
 * retries a refusal reports the status of the attempt that failed alongside the
 * content of the one that succeeded, and reading that as a block throws away a
 * good capture and pauses collection for nothing. A challenge page cannot fake
 * its way past this: it replaces the results, so it has no cards, and the page's
 * own card floor (`minProductCount`) catches a partial render.
 *
 * `captcha` and `refused` are named apart because they read differently in an
 * alert: a challenge page is something a solver might clear, a 429 is a rate
 * limit that only time and fewer requests clear.
 *
 * Lives in shared because two gates read it — the collector deciding whether to
 * stop mid-run, and the API deciding a capture's quality. They were written
 * separately once and drifted: the API learned to read the grid first while the
 * collector kept failing runs on the challenge flag alone, so a page that came
 * back with 73 cards after a solved challenge killed the rest of the run.
 */
export function searchCaptureRefusal(
  capture: SearchCaptureBlockSignal
): "captcha" | "refused" | null {
  if (capture.gridFound) {
    return null
  }
  if (capture.antiBot.captchaDetected) {
    return "captcha"
  }
  return capture.antiBot.httpStatus !== null && REFUSED_HTTP_STATUS.has(capture.antiBot.httpStatus)
    ? "refused"
    : null
}

/** How many consecutive refused vendor sessions count as the site blocking us
 * rather than one session going wrong. Two is the smallest number that can show
 * persistence at all, and persistence is what a block is: measured, 25 keywords
 * sharing one session were refused continuously from roughly the halfway mark,
 * while the same 25 as separate sessions came back clean. */
const BLOCK_PERSISTENCE_SESSIONS = 2

/** A capture plus the vendor session it came from — the least a caller needs for
 * the block question. Both the collector (holding vendor rows) and the API
 * (holding an imported run) can produce it. */
export type SearchSessionCapture = SearchCaptureBlockSignal & {
  collection?: SearchCaptureCollection | null | undefined
}

/**
 * Whether the site turned us away, as opposed to one vendor session going wrong.
 *
 * Reads a run as an ordered series of independent sessions and asks whether the
 * site ever recovered. One owner for a question three places ask: the collector
 * deciding whether to stop paying for more batches, the tick deciding whether
 * re-requesting a resumed run's missing phrases would walk back into a block, and
 * the API deciding the stored run's status.
 *
 * A capture with no recorded session makes the whole run one session, which is the
 * stricter answer — unknown provenance must not be how a real block slips past.
 */
export function searchSiteTurnedUsAway(captures: readonly SearchSessionCapture[]): boolean {
  const refused = captures.map((capture) => searchCaptureRefusal(capture) !== null)
  if (!refused.includes(true)) {
    return false
  }

  const sessions = new Map<string, { index: number; refused: boolean }>()
  for (const [at, capture] of captures.entries()) {
    const collection = capture.collection
    if (collection === null || collection === undefined) {
      return true
    }
    const session = sessions.get(collection.id) ?? { index: collection.index, refused: false }
    session.refused = session.refused || refused[at] === true
    sessions.set(collection.id, session)
  }

  const ordered = [...sessions.values()].sort((left, right) => left.index - right.index)
  return searchBlockBreakerTrips(
    ordered.map((session) => session.refused),
    ordered.length
  )
}

/**
 * Whether the anti-bot breaker should pause collection.
 *
 * Reads the run as an ordered series of independent vendor sessions and trips
 * when the site never recovered: either the last `BLOCK_PERSISTENCE_SESSIONS`
 * sessions in a row were refused, or every session the run had was. A refusal
 * with successful sessions after it is one session going wrong, not a site
 * turning us away — pausing on it costs a day of collection for nothing.
 *
 * The second branch is what keeps a whole-set profile's behavior identical: with
 * one session per run, any refusal is every session refused, so it still trips.
 *
 * `totalSessions` is the number of sessions in play — planned, for a collector
 * deciding mid-run whether to stop; observed, for the API judging a finished
 * run. Both pass it explicitly so neither has to guess the other's meaning.
 */
export function searchBlockBreakerTrips(
  sessionsRefused: readonly boolean[],
  totalSessions: number
): boolean {
  let streak = 0
  for (let at = sessionsRefused.length - 1; at >= 0 && sessionsRefused[at] === true; at -= 1) {
    streak += 1
  }
  if (streak === 0) {
    return false
  }
  return streak >= Math.min(BLOCK_PERSISTENCE_SESSIONS, Math.max(totalSessions, 1))
}

export type SearchRankingRunImportReceipt = {
  marketplace: string
  runId: string
  status: SearchRankingRunStatus
  expectedKeywordCount: number
  attemptedKeywordCount: number
  validKeywordCount: number
  failedKeywordCount: number
  observationCount: number
  qualityReasons: string[]
  alreadyImported: boolean
}

export type SearchRankingDay = {
  /** ISO date (YYYY-MM-DD) of the capture day. */
  day: string
  /** False when no capture that day found the result grid. */
  measured: boolean
  bestOrganicRank: number | null
  bestSponsoredRank: number | null
}

/** One capture's ranks, not merged with anything. The collection profile runs
 *  several times a day, so a day holds several of these — the keyword detail
 *  plots them individually instead of collapsing them, which is the only way an
 *  intra-day move is visible at all. */
export type SearchRankingCapturePoint = {
  /** The run this capture belongs to. Doubles as the selector for viewing that
   *  capture's own first-page snapshot instead of the latest one. */
  runId: string
  /** Timestamp of the capture this point was measured from. */
  capturedAt: string
  /** ISO date (YYYY-MM-DD) of `capturedAt` in the marketplace's reporting time
   *  zone — the day this point belongs to for range filtering. */
  day: string
  /** False when this capture did not find a usable result grid. */
  measured: boolean
  bestOrganicRank: number | null
  bestSponsoredRank: number | null
}

export type SearchRankingProductPoint = {
  runId: string
  capturedAt: string
  day: string
  organicRank: number | null
  sponsoredRank: number | null
}

export type SearchRankingProductHistory = {
  /** Stable identity of the ranked marketplace product. This is not the offer
   * key used to attribute a seller listing to our SKU. */
  rankProductKey: string
  name: string
  url: string
  matchedSku: string | null
  points: SearchRankingProductPoint[]
}

export type SearchRankingLatestCapture = {
  runId: string
  capturedAt: string
  /** False when this capture failed its own quality check (blocked, no grid,
   *  parser miss). Rank fields are then meaningless — render a gap, not a drop. */
  measured: boolean
  productCount: number
  bestOrganicRank: number | null
  bestSponsoredRank: number | null
  ownBrandCount: number
}

export type SearchRankingKeywordSummary = {
  phrase: string
  category: string
  latest: SearchRankingLatestCapture | null
  /** Oldest→newest, one entry per capture day, carrying that day's **last**
   *  capture — the row's sparkline and Change must agree with `latest`, and a
   *  best-of-day merge would hide a drop that happened during the day. Open the
   *  keyword for every capture. */
  history: SearchRankingDay[]
}

export type SearchRankingMarketplace = {
  id: string
  name: string
  country: string
  sponsoredLabel: string
  /** Opens the tracker when no marketplace is named. Exactly one marketplace
   *  carries it, and clients pick it by name rather than by list position — a
   *  landing page that depended on sort order would move whenever the sort did. */
  primary: boolean
  /** The zone capture days are bucketed in. Clients must render capture clock
   *  times in it too — a day boundary in Bratislava and a time in UTC would put
   *  a 00:30 local capture on the previous day's clock. */
  reportingTimeZone: string
  collectionProfile: {
    id: string
    tag: string
    geoCountry: string
  }
}

export type SearchRankingOverview = {
  marketplace: SearchRankingMarketplace
  /** The newest persisted attempt for this marketplace's active collection
   * profile. It is intentionally independent of keyword/capture usability so a
   * blocked or invalid scrape cannot hide behind older rank evidence. */
  latestRun: {
    completedAt: string
    status: SearchRankingRunStatus
  } | null
  keywords: SearchRankingKeywordSummary[]
}

export type SearchRankingCard = {
  screenSlot: number
  organicRank: number | null
  sponsoredRank: number | null
  sponsored: boolean
  isOwnBrand: boolean
  externalProductKey: string | null
  /** Stable marketplace-product identity used by product history. This can
   * differ from `externalProductKey`, which identifies the attributed seller
   * offer on marketplaces such as Allegro. */
  rankProductKey: string | null
  name: string
  url: string
  priceCurrent: number | null
  priceOriginal: number | null
  currency: string | null
  /** Movement of the card's channel rank (organic rank for organic cards,
   *  sponsored slot order for paid cards) vs the previous measured day;
   *  positive = moved up. Null when unmatched or no previous capture. */
  rankChange: number | null
  /** True when a previous capture exists and this card wasn't on it. */
  isNew: boolean
  /** Our catalog config SKU this own-brand card matched, resolved once when the
   * evidence was imported. Null for competitor cards and for own-brand
   * listings we could not match. */
  matchedSku: string | null
}

export type SearchRankingMarketplaceList = {
  marketplaces: SearchRankingMarketplace[]
}

/** Per-product view: latest ranks of marketplace listings matched to one of
 * our catalog products. Matches remain visible so a wrong match is not silent. */
export type SearchProductRankings = {
  marketplace: SearchRankingMarketplace
  sku: string
  matches: { externalProductKey: string; name: string; url: string }[]
  rankings: {
    phrase: string
    category: string
    bestOrganicRank: number | null
    bestSponsoredRank: number | null
  }[]
}

export type SearchRankingKeywordDetail = {
  marketplace: SearchRankingMarketplace
  phrase: string
  category: string
  runId: string
  capturedAt: string
  /** The exact SRP URL this capture was taken from, so the page behind a rank is
   *  one click away and is the URL we actually measured — not one rebuilt from
   *  the phrase. */
  searchUrl: string
  /** Honest inclusive calendar bounds of the API's fixed read window. Date
   * controls use these rather than pretending the first captured day is the
   * start of all available evidence. */
  availableRange: { from: string; to: string }
  /** Oldest→newest, one entry per capture — several per day. */
  history: SearchRankingCapturePoint[]
  /** One series per stable marketplace product id. A card without a rank key
   * remains in the snapshot but cannot be joined across captures. */
  productHistory: SearchRankingProductHistory[]
  cards: SearchRankingCard[]
}

export type SearchRankingTitleOpportunityExample = {
  name: string
  url: string
  externalProductKey: string | null
  bestScreenPosition: number
}

export type SearchRankingTitleOpportunity = {
  phrase: string
  measuredDayCount: number
  competitorProductCount: number
  bestScreenPosition: number
  organic: boolean
  sponsored: boolean
  examples: SearchRankingTitleOpportunityExample[]
}

export type SearchRankingTitleOpportunities = {
  marketplace: SearchRankingMarketplace
  phrase: string
  from: string
  to: string
  opportunities: SearchRankingTitleOpportunity[]
}
